import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Card, CardContent } from "@/components/ui/card";
import { Store, ChevronRight } from "lucide-react";
import { useCurrentLocation } from "@/hooks/use-current-location";

export const Route = createFileRoute("/_cashier/select")({
  component: CashierSelectPage,
});

function CashierSelectPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { locations, loading, switchTo } = useCurrentLocation();

  // If user only has one location, skip the picker
  useEffect(() => {
    if (!loading && locations.length === 1) {
      switchTo(locations[0].id);
      navigate({ to: "/cashier" });
    }
  }, [loading, locations, navigate, switchTo]);

  const choose = (id: string) => {
    switchTo(id);
    navigate({ to: "/cashier" });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">{t("cashier.loading")}</div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-3rem)] bg-background flex items-start justify-center px-4 py-10">
      <div className="w-full max-w-md space-y-5">
        <div className="text-center">
          <h1 className="text-2xl font-display font-bold">{t("cashier.selectTitle")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{t("cashier.selectSubtitle")}</p>
        </div>

        <div className="space-y-2">
          {locations.map((loc) => (
            <button
              key={loc.id}
              onClick={() => choose(loc.id)}
              className="w-full text-left"
            >
              <Card className="hover:border-primary transition-colors cursor-pointer">
                <CardContent className="p-4 flex items-center gap-3">
                  <div className="size-10 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <Store className="size-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{loc.business_name}</p>
                    <p className="text-xs text-muted-foreground capitalize">{loc.role === "owner" ? t("cashier.roleOwner") : t("cashier.roleCashier")}</p>
                  </div>
                  <ChevronRight className="size-5 text-muted-foreground shrink-0" />
                </CardContent>
              </Card>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
